import { Box, ButtonBase, Stack, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import NotificationsOffRounded from "@mui/icons-material/NotificationsOffRounded";
import { usePWA } from "../contexts/PWAContext";
import { CARD_STYLES } from "../constants/styles";

const NotificationPermissionsBanner = ({
    onRequestPermission,
}: {
    onRequestPermission: () => void;
}) => {
    const theme = useTheme();
    const primary = theme.palette.primary.main;
    const { isStandalone, isIos, isAppleDesktop } = usePWA();

    const initialPermission =
        typeof Notification !== "undefined"
            ? Notification.permission
            : "granted";

    const [permissionStatus, setPermissionStatus] =
        useState<NotificationPermission>(initialPermission);

    const checkPermission = useCallback(() => {
        if (typeof Notification === "undefined") return;
        const currentPermission = Notification.permission;
        if (currentPermission !== permissionStatus) {
            setPermissionStatus(currentPermission);
        }
    }, [permissionStatus]);

    useEffect(() => {
        if (typeof Notification === "undefined") return;
        const intervalId = setInterval(checkPermission, 1000);
        return () => clearInterval(intervalId);
    }, [checkPermission]);

    const isDenied = permissionStatus === "denied";
    const isGranted = permissionStatus === "granted";

    const supportUrl = useMemo(() => {
        if (isIos() && isStandalone()) {
            return "https://support.apple.com/en-us/HT201925";
        } else if (isAppleDesktop() || (isIos() && !isStandalone())) {
            return "https://support.apple.com/guide/safari/manage-website-notifications-sfri40734/mac";
        } else if (isStandalone()) {
            return "https://support.google.com/android/answer/9079661";
        }
        return "https://support.google.com/chrome/answer/3220216";
    }, [isIos, isAppleDesktop, isStandalone]);

    if (isGranted || typeof Notification === "undefined") return null;

    return (
        <Stack sx={{ ...CARD_STYLES, bgcolor: alpha(theme.palette.warning.main, 0.06) }} spacing={1.5}>
            <Stack direction="row" alignItems="center" spacing={1}>
                <Box
                    sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "10px",
                        bgcolor: alpha(theme.palette.warning.main, 0.12),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flex: "0 0 auto",
                    }}
                >
                    <NotificationsOffRounded
                        sx={{ fontSize: 18, color: theme.palette.warning.dark }}
                    />
                </Box>
                <Typography fontWeight={600} fontSize="13px" color="text.primary">
                    Push notifications {isDenied ? "blocked" : "disabled"}
                </Typography>
            </Stack>
            <Typography fontSize="12px" lineHeight="17px" color="text.secondary">
                {isDenied
                    ? "Notifications are blocked in your browser settings. Enable them to stay updated with bookings and messages."
                    : "Enable push notifications to get real-time alerts for bookings, messages, and updates."}
            </Typography>
            <ButtonBase
                onClick={
                    isDenied
                        ? () => window.open(supportUrl, "_blank")
                        : onRequestPermission
                }
                sx={{
                    alignSelf: "flex-start",
                    px: 2,
                    py: 0.75,
                    borderRadius: "12px",
                    bgcolor: alpha(primary, 0.08),
                    gap: 0.75,
                    display: "flex",
                    alignItems: "center",
                }}
            >
                <Typography fontWeight={500} fontSize="13px" color={primary}>
                    {isDenied ? "Learn how to enable" : "Enable"}
                </Typography>
            </ButtonBase>
        </Stack>
    );
};

export default NotificationPermissionsBanner;
